import { getAnalyticsId } from '@shared/core/analytics/segment/getAnalyticsId';
import { NR_ATTR_ANALYTICS_ID } from '@shared/utils/logger';
import { isChunkLoadErrorWhileUnloading } from '@shared/utils/pageUnloading';
import { isReportedError } from '@shared/utils/reportedErrors';
import { withWindow } from '@shared/utils/withWindow';
import { config } from '@static/js/env.config';

export const initNewRelic = () => {
  withWindow(window => {
    const { newrelic, buildNumber } = window;

    if (newrelic) {
      newrelic.setCustomAttribute('buildNumber', buildNumber);
      newrelic.setCustomAttribute('env', config.joyEnvironment);
      newrelic.setCustomAttribute(NR_ATTR_ANALYTICS_ID, getAnalyticsId());
      newrelic.addRelease('joy-web', buildNumber);
    }
  });
};

const URL_IN_TEXT = /[a-z][a-z\d+.-]*:\/\//i;

/**
 * The browser hides errors from cross-origin scripts loaded without CORS behind a bare "Script error."
 * with no filename or stack, so NR has nothing to group or act on.
 */
const isOpaqueScriptError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { message, sourceURL, line, stack } = error as Partial<Record<'message' | 'sourceURL' | 'line' | 'stack', unknown>>;
  return message === 'Script error.' && !sourceURL && !line && !(typeof stack === 'string' && URL_IN_TEXT.test(stack));
};

// Segment's GA4 destination loads gtag through this first-party proxy path; its load failures reject inside Segment's code.
const ANALYTICS_PROXY_PATH = '/_p/metrics/';

const ANALYTICS_PROXY_LOAD_FAILURE = new RegExp(`^(Unhandled Promise Rejection: )?Failed to load https?://[^/]+${ANALYTICS_PROXY_PATH}`);

const isAnalyticsProxyLoadFailure = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && ANALYTICS_PROXY_LOAD_FAILURE.test(message);
};

/** Safari's "runtime.sendMessage" and "No Listener: tabs:" rejections come only from extension messaging APIs. */
const EXTENSION_ONLY_MESSAGE =
  /^(Unhandled Promise Rejection: )?(Failed to connect to MetaMask|Extension context invalidated\.|Invalid call to runtime\.sendMessage\(\)\. Tab not found\.|No Listener: tabs:[\w.-]+)$/;

const EXTENSION_SCHEMES = new Set(['chrome-extension', 'moz-extension', 'safari-extension', 'safari-web-extension', 'webkit-masked-url']);

const CHROME_FRAME = /^\s*at\s/;

/** Firefox and Safari frames are "fn@location:line:col"; Safari names can hold spaces ("global code@..."). */
const AT_SIGN_FRAME = /^[^@]*@\S+:\d+(:\d+)?$/;

/** A location ending in ":line" or ":line:col", optionally inside Chrome's parentheses. */
const LOCATED_FRAME = /:\d+(:\d+)?\)?$/;

/** Engine-internal locations say nothing about whose code ran, so the next frame decides. */
const NEUTRAL_LOCATION = /[\s(@]<(anonymous|inline)>(:\d+(:\d+)?)?\)?$/;

const MAX_FRAME_LINE_LENGTH = 2000;

const SCHEME_CHAR = /[a-z\d+.-]/i;

/** Linear scan: every "://" is read back to the start of its scheme, and scheme chars never include ":" or "/". */
const schemesIn = (line: string): string[] => {
  const schemes: string[] = [];
  for (let separator = line.indexOf('://'); separator !== -1; separator = line.indexOf('://', separator + 3)) {
    let start = separator;
    while (start > 0 && SCHEME_CHAR.test(line[start - 1])) start--;
    schemes.push(line.slice(start, separator).toLowerCase());
  }
  return schemes;
};

const isFrameLine = (line: string): boolean => CHROME_FRAME.test(line) || AT_SIGN_FRAME.test(line);

/** Only Chrome-style stacks open with "name: message"; skip all of it so a message can't plant a fake frame above the real ones. */
const framesOf = (stack: string, message: unknown): string[] => {
  const lines = stack.split('\n');
  if (CHROME_FRAME.test(lines[0]) || !lines.some(line => CHROME_FRAME.test(line))) return lines;
  return lines.slice(typeof message === 'string' ? message.split('\n').length : 1);
};

/** `undefined` when the stack has no located frame to judge by. */
const isExtensionTopFrame = (stack: string, message: unknown): boolean | undefined => {
  for (const rawLine of framesOf(stack, message)) {
    // A line too long to read safely might be the top frame, so it can't prove extension code.
    if (rawLine.length > MAX_FRAME_LINE_LENGTH) return false;
    const line = rawLine.trimEnd();
    if (!isFrameLine(line)) continue;
    const schemes = schemesIn(line);
    if (schemes.length > 0) return schemes.every(scheme => EXTENSION_SCHEMES.has(scheme));
    if (LOCATED_FRAME.test(line) && !NEUTRAL_LOCATION.test(line)) return false;
  }
  return undefined;
};

const isExtensionUrl = (url: unknown): boolean => {
  if (typeof url !== 'string') return false;
  const separator = url.indexOf('://');
  return separator > 0 && EXTENSION_SCHEMES.has(url.slice(0, separator).toLowerCase());
};

/**
 * Extensions inject scripts into our pages, and NR attributes their failures to us. NR hands us the raw error, so its top located frame
 * names the code that created it; without one (e.g. NR's `UncaughtError` for Safari's masked extension scripts) `sourceURL` decides.
 * Errors we reported ourselves are always kept: an extension wrapping `fetch` can be the top frame on our own failed requests.
 */
const isBrowserExtensionError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || isReportedError(error)) return false;
  const { message, stack, sourceURL, filename } = error as Partial<Record<'message' | 'stack' | 'sourceURL' | 'filename', unknown>>;
  if (typeof message === 'string' && EXTENSION_ONLY_MESSAGE.test(message)) return true;
  const topFrameVerdict = typeof stack === 'string' ? isExtensionTopFrame(stack, message) : undefined;
  return topFrameVerdict ?? (isExtensionUrl(sourceURL) || isExtensionUrl(filename));
};

/**
 * Only the engines' "missing global" messages naming `name` (Safari, Chrome/Firefox, Safari's property read), so an error that just
 * mentions the name in a URL or text is kept. `name` must be a plain identifier.
 */
const injectedGlobal = (name: string): RegExp =>
  new RegExp(
    `^(Unhandled Promise Rejection: )?(Can't find variable: ${name}|${name} is not defined|(undefined|null) is not an object \\(evaluating '(window\\.)?${name}([^\\w$'][^']*)?'\\))$`,
  );

const HOST_APP_INJECTED_MESSAGES = [
  // Windows embedded-browser host bridge (Microsoft WebView, e.g. link scanners).
  /^(Unhandled Promise Rejection: )?Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/,
  // iOS in-app browsers' injected autofill.
  injectedGlobal('_AutofillCallbackHandler'),
  // Zalo in-app browser.
  injectedGlobal('zaloJSV2'),
  // Firefox for iOS reader-mode and page scripts.
  injectedGlobal('__firefox__'),
  // Chrome for iOS page scripts.
  injectedGlobal('__gCrWeb'),
  // Snapchat in-app browser.
  injectedGlobal('SCDynimacBridge'),
];

/**
 * Host apps and in-app browsers inject scripts into our pages that call into their native side, and NR attributes their failures to us.
 * None of these names exist in our code. Errors we reported ourselves are always kept.
 */
const isHostAppInjectedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || isReportedError(error)) return false;
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && HOST_APP_INJECTED_MESSAGES.some(pattern => pattern.test(message));
};

/** Spec-defined warnings (the browser defers the notifications one frame) that Chrome and Safari surface as window error events with no stack. */
const RESIZE_OBSERVER_LOOP = /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

const isResizeObserverLoopError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && RESIZE_OBSERVER_LOOP.test(message);
};

const isUnreportableError = (error: unknown): boolean =>
  isChunkLoadErrorWhileUnloading(error) ||
  isOpaqueScriptError(error) ||
  isAnalyticsProxyLoadFailure(error) ||
  isBrowserExtensionError(error) ||
  isHostAppInjectedError(error) ||
  isResizeObserverLoopError(error);

/**
 * Covers errors NR captures on its own (unhandled rejections, window errors), not just the ones we notice.
 * NR keeps only one error handler, so every filter goes through `isUnreportableError`.
 */
export const ignoreUnreportableErrors = () => {
  withWindow(window => {
    window.newrelic?.setErrorHandler(isUnreportableError);
  });
};
